type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type LooseRecord = Record<string, any>;

const ATTEMPT_KEY_PREFIX = 'qm_project_duplicate_attempt_v1:';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const fallbackAttempts = new Map<string, string>();

const KNOWN_PRECOMMIT_CODES = new Set([
    'DOCUMENT_DURABILITY_REQUIRED',
    'MEDIA_DURABILITY_REQUIRED',
    'CLIENT_PAYLOAD_TOO_LARGE',
    'INLINE_CUSTOMER_MEDIA_FORBIDDEN',
    'DUPLICATE_LOCAL_MEDIA_UNAVAILABLE',
    'DUPLICATE_TARGET_ID_INVALID',
    'DUPLICATE_SOURCE_VERSION_REQUIRED',
    'PROJECT_NOT_FOUND',
    'PROJECT_STALE_WRITE',
    'DUPLICATE_CLOUD_MEDIA_UNAVAILABLE',
    'DUPLICATE_CLOUD_MEDIA_INVALID',
    'DUPLICATE_CLOUD_MEDIA_COPY_FAILED',
    'DUPLICATE_PROJECT_SAVE_FAILED',
    'DUPLICATE_TARGET_EXISTS',
]);

function attemptKey(sourceProjectId: string): string {
    return `${ATTEMPT_KEY_PREFIX}${sourceProjectId}`;
}
function browserSessionStorage(): StorageLike | null {
    try {
        return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    } catch {
        return null;
    }
}

function storedTargetId(sourceProjectId: string, storage: StorageLike | null): string | null {
    const key = attemptKey(sourceProjectId);
    try {
        const value = storage?.getItem(key);
        if (value && UUID_RE.test(value)) return value;
    } catch { /* in-memory fallback below */ }
    const fallback = fallbackAttempts.get(sourceProjectId);
    return fallback && UUID_RE.test(fallback) ? fallback : null;
}

/**
 * Claim the stable target id for one not-yet-resolved duplicate attempt.
 * A retry in the same browser tab reuses this UUID, allowing the backend to
 * return the already-committed target rather than creating a hidden twin.
 */
export function claimProjectDuplicateAttempt(
    sourceProjectId: string,
    makeTargetId: () => string = () => crypto.randomUUID(),
    storage: StorageLike | null = browserSessionStorage(),
): string {
    if (!UUID_RE.test(String(sourceProjectId || ''))) {
        throw new TypeError('claimProjectDuplicateAttempt requires a UUID source project id');
    }
    const existing = storedTargetId(sourceProjectId, storage);
    if (existing) return existing;
    const targetProjectId = makeTargetId();
    if (!UUID_RE.test(String(targetProjectId || ''))) {
        throw new TypeError('Duplicate target id generator returned an invalid UUID');
    }
    fallbackAttempts.set(sourceProjectId, targetProjectId);
    try { storage?.setItem(attemptKey(sourceProjectId), targetProjectId); } catch { /* fallback is sufficient */ }
    return targetProjectId;
}

/** Clear only the attempt the caller actually resolved (compare-and-delete). */
export function clearProjectDuplicateAttempt(
    sourceProjectId: string,
    targetProjectId: string,
    storage: StorageLike | null = browserSessionStorage(),
): void {
    if (storedTargetId(sourceProjectId, storage) !== targetProjectId) return;
    if (fallbackAttempts.get(sourceProjectId) === targetProjectId) fallbackAttempts.delete(sourceProjectId);
    try {
        if (storage?.getItem(attemptKey(sourceProjectId)) === targetProjectId) {
            storage.removeItem(attemptKey(sourceProjectId));
        }
    } catch { /* fallback was already cleared */ }
}

export function isKnownProjectDuplicatePreCommitFailure(error: unknown): boolean {
    const candidate = error as LooseRecord | null;
    return !!candidate && KNOWN_PRECOMMIT_CODES.has(String(candidate.code || ''));
}

export interface ReconcileProjectDuplicateFailureInput {
    sourceProjectId: string;
    targetProjectId: string;
    duplicateSaveStarted: boolean;
    error: unknown;
    lookupTarget: (targetProjectId: string) => Promise<LooseRecord | null | undefined>;
    cleanupTarget: (targetProjectId: string) => Promise<unknown>;
    clearAttempt: (sourceProjectId: string, targetProjectId: string) => void;
}

export type ProjectDuplicateFailureDisposition =
    | { disposition: 'committed'; project: LooseRecord }
    | { disposition: 'rolled_back'; cleanup: unknown }
    | { disposition: 'uncertain' };

/**
 * Resolve duplicate failure without guessing. A received pre-commit refusal
 * rolls back local clones. An ambiguous response is reconciled by its stable
 * target UUID; only a confirmed 404 proves rollback is safe. Network ambiguity
 * retains both the UUID and target-owned local recovery media for retry.
 */
export async function reconcileProjectDuplicateFailure(
    input: ReconcileProjectDuplicateFailureInput,
): Promise<ProjectDuplicateFailureDisposition> {
    const rollback = async (): Promise<ProjectDuplicateFailureDisposition> => {
        const cleanup = await input.cleanupTarget(input.targetProjectId);
        input.clearAttempt(input.sourceProjectId, input.targetProjectId);
        return { disposition: 'rolled_back', cleanup };
    };

    if (!input.duplicateSaveStarted || isKnownProjectDuplicatePreCommitFailure(input.error)) {
        return rollback();
    }

    try {
        const project = await input.lookupTarget(input.targetProjectId);
        if (project && String(project.id || '') === input.targetProjectId) {
            input.clearAttempt(input.sourceProjectId, input.targetProjectId);
            return { disposition: 'committed', project };
        }
        return { disposition: 'uncertain' };
    } catch (lookupError) {
        const candidate = lookupError as LooseRecord | null;
        if (candidate?.status === 404 || candidate?.code === 'PROJECT_NOT_FOUND') {
            return rollback();
        }
        return { disposition: 'uncertain' };
    }
}
